import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import RichTextEditor from '../components/RichTextEditor';
import api from '../utils/api';
import './StaffFAQManagementPage.css';

const EMPTY_FORM = {
  title: '',
  content: '',
  tags: '',
  status: 'draft',
  priority: 'normal',
  visibleTo: ['public'],
  publishDate: '',
  expiryDate: ''
};

const StaffFAQManagementPage = () => {
  const { currentUser } = useContext(AuthContext);
  const [resolvedUser, setResolvedUser] = useState(currentUser);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [modalShake, setModalShake] = useState(false);

  const handleBackdropClick = () => {
    setModalShake(true);
    setTimeout(() => setModalShake(false), 500);
  };

  // Fetch fresh user data so permission changes take effect without requiring re-login
  useEffect(() => {
    let mounted = true;
    api.get('/api/users/me')
      .then(res => { if (mounted && res?.data) setResolvedUser(res.data); })
      .catch(() => { if (mounted) setResolvedUser(currentUser); });
    return () => { mounted = false; };
  }, [currentUser]);

  const pageUser = resolvedUser || currentUser;
  const isAdmin = pageUser?.userType === 'admin';
  const canAccess = isAdmin || pageUser?.faqEditor === true;

  useEffect(() => {
    if (canAccess) fetchPosts();
    else setLoading(false);
  }, [canAccess]);

  const fetchPosts = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/api/faq/admin');
      setPosts(res.data.posts || []);
    } catch (err) {
      setError('Failed to load FAQ posts.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const openNew = () => {
    setEditingId(null);
    setForm({ ...EMPTY_FORM, publishDate: new Date().toISOString().slice(0, 16) });
    setSaveError('');
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingId(post._id);
    setForm({
      title: post.title || '',
      content: post.content || '',
      tags: (post.tags || []).join(', '),
      status: post.status || 'draft',
      priority: post.priority || 'normal',
      visibleTo: post.visibleTo || ['public'],
      publishDate: post.publishDate ? new Date(post.publishDate).toISOString().slice(0, 16) : '',
      expiryDate: post.expiryDate ? new Date(post.expiryDate).toISOString().slice(0, 16) : ''
    });
    setSaveError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    setSaveError('');
  };

  const handleVisibleToChange = (tier) => {
    const current = form.visibleTo;
    const updated = current.includes(tier)
      ? current.filter(t => t !== tier)
      : [...current, tier];
    setForm(f => ({ ...f, visibleTo: updated }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      setSaveError('Title and content are required.');
      return;
    }

    setSaving(true);
    setSaveError('');
    try {
      const payload = {
        title: form.title.trim(),
        content: form.content,
        tags: form.tags.split(',').map(t => t.trim().toLowerCase()).filter(Boolean),
        status: form.status,
        priority: form.priority,
        visibleTo: form.visibleTo.length > 0 ? form.visibleTo : ['public'],
        publishDate: form.publishDate || null,
        expiryDate: form.expiryDate || null
      };

      if (editingId) {
        await api.put(`/api/faq/${editingId}`, payload);
      } else {
        await api.post('/api/faq', payload);
      }

      closeForm();
      await fetchPosts();
    } catch (err) {
      setSaveError(err.response?.data?.message || 'Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/faq/${id}`);
      setDeleteConfirm(null);
      await fetchPosts();
    } catch (err) {
      alert('Failed to delete post. ' + (err.response?.data?.message || ''));
    }
  };

  const statusBadge = (status) => {
    const map = { published: 'badge-published', draft: 'badge-draft', archived: 'badge-archived' };
    return <span className={`faq-mgmt-badge ${map[status] || ''}`}>{status}</span>;
  };

  return (
    <div className="faq-mgmt-page">
      {!canAccess ? (
        <div className="faq-mgmt-error" style={{ marginTop: '2rem' }}>
          You do not have the <strong>FAQ Editor</strong> permission. Contact an admin to enable it for your account.
        </div>
      ) : (
      <>
      <div className="faq-mgmt-header">
        <div>
          <h2>FAQ Management</h2>
          <p>Create and manage HTML-rich FAQ posts visible to public, member, citizen, or staff tiers.</p>
        </div>
        <button className="btn-primary" onClick={openNew}>+ New FAQ Post</button>
      </div>

      {error && <div className="faq-mgmt-error">{error}</div>}

      {/* ── Post Table ── */}
      {loading ? (
        <div className="faq-mgmt-loading"><div className="spinner" /><p>Loading...</p></div>
      ) : posts.length === 0 ? (
        <div className="faq-mgmt-empty">
          <p>No FAQ posts yet. Create one to get started.</p>
        </div>
      ) : (
        <div className="faq-mgmt-table-wrap">
          <table className="faq-mgmt-table">
            <thead>
              <tr>
                <th>Question (Title)</th>
                <th>Status</th>
                <th>Priority</th>
                <th>Visible To</th>
                <th>Tags</th>
                <th>Publish Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map(post => (
                <tr key={post._id}>
                  <td className="faq-mgmt-title-cell">{post.title}</td>
                  <td>{statusBadge(post.status)}</td>
                  <td><span className={`faq-priority faq-priority-${post.priority}`}>{post.priority}</span></td>
                  <td>{(post.visibleTo || []).join(', ')}</td>
                  <td>{(post.tags || []).join(', ') || '—'}</td>
                  <td>{post.publishDate ? new Date(post.publishDate).toLocaleDateString() : '—'}</td>
                  <td className="faq-mgmt-actions">
                    <button className="btn-edit" onClick={() => openEdit(post)}>Edit</button>
                    {isAdmin && (
                      <button className="btn-delete" onClick={() => setDeleteConfirm(post._id)}>Delete</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Delete Confirmation ── */}
      {deleteConfirm && (
        <div className="faq-mgmt-overlay">
          <div className="faq-mgmt-modal faq-confirm-modal">
            <h3>Delete FAQ Post?</h3>
            <p>This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setDeleteConfirm(null)}>Cancel</button>
              <button className="btn-delete" onClick={() => handleDelete(deleteConfirm)}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Create / Edit Form ── */}
      {showForm && (
        <div className="faq-mgmt-overlay" onClick={handleBackdropClick}>
          <div className={`faq-mgmt-modal faq-form-modal${modalShake ? ' modal-shake' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Edit FAQ Post' : 'New FAQ Post'}</h3>
              <button className="modal-close" onClick={closeForm}>×</button>
            </div>

            <form onSubmit={handleSubmit} className="faq-form">
              {/* Title */}
              <div className="form-group">
                <label>Question / Title *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. How do I apply for the education grant?"
                  required
                />
              </div>

              {/* HTML Content via RichTextEditor */}
              <div className="form-group">
                <label>Answer (HTML content) *</label>
                <RichTextEditor
                  value={form.content}
                  onChange={content => setForm(f => ({ ...f, content }))}
                  placeholder="Write the full answer here. You can include formatted text, lists, links, and HTML."
                />
              </div>

              {/* Two-column row */}
              <div className="form-row">
                <div className="form-group">
                  <label>Status</label>
                  <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
                    <option value="draft">Draft</option>
                    <option value="published">Published</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priority</label>
                  <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Tags */}
              <div className="form-group">
                <label>Tags / Category (comma-separated)</label>
                <input
                  type="text"
                  value={form.tags}
                  onChange={e => setForm(f => ({ ...f, tags: e.target.value }))}
                  placeholder="e.g. education, grants, eligibility"
                />
              </div>

              {/* Visible To */}
              <div className="form-group">
                <label>Visible To</label>
                <div className="faq-visible-checks">
                  {['public', 'user', 'citizen', 'staff'].map(tier => (
                    <label key={tier} className="faq-vis-item">
                      <input
                        type="checkbox"
                        checked={form.visibleTo.includes(tier)}
                        onChange={() => handleVisibleToChange(tier)}
                      />
                      <span>{tier.charAt(0).toUpperCase() + tier.slice(1)}</span>
                    </label>
                  ))}
                </div>
                <small className="form-hint">Select all tiers that should see this FAQ.</small>
              </div>

              {/* Dates */}
              <div className="form-row">
                <div className="form-group">
                  <label>Publish Date</label>
                  <input
                    type="datetime-local"
                    value={form.publishDate}
                    onChange={e => setForm(f => ({ ...f, publishDate: e.target.value }))}
                  />
                </div>
                <div className="form-group">
                  <label>Expiry Date (optional)</label>
                  <input
                    type="datetime-local"
                    value={form.expiryDate}
                    onChange={e => setForm(f => ({ ...f, expiryDate: e.target.value }))}
                  />
                </div>
              </div>

              {saveError && <div className="faq-save-error">{saveError}</div>}

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingId ? 'Save Changes' : 'Create FAQ Post'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
    )}
    </div>
  );
};

export default StaffFAQManagementPage;
